// Materials and task value for project topics (db/projects-schema.sql).
//
// A material's cost is the price of one, so its total is cost × qty. A task
// with a cost typed in costs exactly that — it's the whole figure, so the
// materials and sub-tasks under it aren't added on top (that counted them
// twice). A task with no cost of its own costs what's under it, so what you
// list to buy for a job counts toward it.
//
// Value is worked out both ways: gain per $1,000 of that cost, and gain per
// hour of work — "0.3 s laptime per $1k", "4 lb per hr" — so tasks can be
// ranked by either. Gains in different units can't be compared, so each unit
// is ranked on its own.
import { TaskItem } from '../types/tasks';
import { childrenOf } from './taskTree';

/** Material status, from its progress. */
export const MATERIAL_STATUS = [
  { progress: 0, label: 'Needed' },
  { progress: 50, label: 'Ordered' },
  { progress: 100, label: 'Received' },
] as const;

export const isMaterial = (item: TaskItem) => item.node_kind === 'material';

export function materialStatus(item: TaskItem): string {
  return item.progress >= 100 ? 'Received' : item.progress > 0 ? 'Ordered' : 'Needed';
}

/** What a material comes to: price × quantity (null with no price). */
export function materialTotal(item: TaskItem): number | null {
  if (item.cost == null) return null;
  return item.cost * (item.qty ?? 1);
}

/** A topic's own cost: a material's total, a task's cost. */
const ownCost = (item: TaskItem) => (isMaterial(item) ? materialTotal(item) ?? 0 : item.cost ?? 0);

/** Has a cost of its own that stands for the whole job. */
const hasOwnCost = (item: TaskItem) => !isMaterial(item) && item.cost != null;

/**
 * Each topic's cost: its own when it has one, otherwise the sum of what's
 * under it (each of those worked out the same way).
 */
export function costTree(items: TaskItem[]): Map<number, number> {
  const kids = childrenOf(items);
  const out = new Map<number, number>();
  const walk = (item: TaskItem): number => {
    const below = (kids.get(item.id) || []).reduce((sum, c) => sum + walk(c), 0);
    const total = isMaterial(item) ? ownCost(item) + below : hasOwnCost(item) ? item.cost! : below;
    out.set(item.id, total);
    return total;
  };
  (kids.get(null) || []).forEach(walk);
  // Anything whose parent isn't in the set (a branch shown alone).
  items.forEach((i) => !out.has(i.id) && walk(i));
  return out;
}

export interface TaskValue {
  gain: number;
  unit: string;
  /** What it costs, materials and sub-tasks included. */
  cost: number;
  hours: number | null;
  /** Gain per $1,000 (null with no cost) and per hour (null with no hours). */
  perK: number | null;
  perHr: number | null;
}

/** A task's value, or null when it doesn't say what it gains. */
export function valueOf(item: TaskItem, costs: Map<number, number>): TaskValue | null {
  if (item.benefit == null || !item.benefit_unit) return null;
  const cost = costs.get(item.id) ?? ownCost(item);
  const hours = item.hours != null && item.hours > 0 ? item.hours : null;
  return {
    gain: item.benefit,
    unit: item.benefit_unit,
    cost,
    hours,
    perK: cost > 0 ? (item.benefit / cost) * 1000 : null,
    perHr: hours ? item.benefit / hours : null,
  };
}

/** Has a cost or hours to measure its gain against. */
export const isRanked = (v: TaskValue | null): v is TaskValue => !!v && (v.perK !== null || v.perHr !== null);

/** Units already in use, most used first — offered when typing a new one. */
export function unitSuggestions(items: TaskItem[], typed = ''): string[] {
  const counts = new Map<string, { label: string; n: number }>();
  for (const i of items) {
    const u = i.benefit_unit?.trim();
    if (!u) continue;
    const key = u.toLowerCase();
    const c = counts.get(key);
    if (c) c.n++;
    else counts.set(key, { label: u, n: 1 });
  }
  const q = typed.trim().toLowerCase();
  return Array.from(counts.entries())
    .filter(([key]) => !q || (key.includes(q) && key !== q))
    .sort((a, b) => b[1].n - a[1].n)
    .map(([, c]) => c.label)
    .slice(0, 6);
}

/** '$1,240' / '$45.50' */
export function money(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
}

/** A readable number: 3 significant figures for small values, whole for big. */
export function num(n: number): string {
  const abs = Math.abs(n);
  const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : abs >= 1 ? 2 : 3;
  return n.toLocaleString('en-US', { maximumFractionDigits: digits });
}

/** '0.8 s laptime / $1k · 0.05 s laptime / hr' — whichever it has. */
export const valueLabel = (v: TaskValue) =>
  [v.perK !== null && `${num(v.perK)} ${v.unit} / $1k`, v.perHr !== null && `${num(v.perHr)} ${v.unit} / hr`].filter(Boolean).join(' · ');
